import { Controller } from '@hotwired/stimulus'
import { subscribe, unsubscribe } from '@app/adapters/cookie-consent'

// Fires a single Heap event when a report is opened, so we can compare how
// often users open copied reports vs. the original firm templates they were
// copied from. Mounted on an element outside the table turbo-frame so it
// connects once per full-page open, not on every frame sub-request.
//
// Heap is loaded behind OneTrust cookie consent, so on a full page load
// window.heap usually isn't defined yet when this controller connects — it only
// becomes available once consent resolves, asynchronously, after
// DOMContentLoaded. We track immediately when heap is already present (e.g.
// Turbolinks navigations, or consent given on a previous page) and otherwise
// wait for the consent-ready signal, which fires whenever consent lands.
export default class extends Controller {
  static values = {
    name: String,
    locked: Boolean,
    parentId: Number,
    parentLocked: Boolean,
  }

  declare nameValue: string
  declare lockedValue: boolean
  declare parentIdValue: number
  declare readonly hasParentIdValue: boolean
  declare parentLockedValue: boolean

  connect(): void {
    if (window.heap) {
      this.trackOpened()
    } else {
      subscribe(this.onConsentResolved)
    }
  }

  disconnect(): void {
    unsubscribe(this.onConsentResolved)
  }

  private onConsentResolved = (): void => {
    if (!window.heap) return

    this.trackOpened()
    unsubscribe(this.onConsentResolved)
  }

  private trackOpened(): void {
    window.heap?.track('Reports: Report Opened', {
      name: this.nameValue,
      locked: this.lockedValue,
      has_parent: this.hasParentIdValue,
      parent_id: this.hasParentIdValue ? this.parentIdValue : null,
      parent_locked: this.parentLockedValue,
    })
  }
}
